import React, { useState } from 'react';
import { PlusIcon, TrashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type { SpreadsheetRef } from '../types';

interface SpreadsheetIdEditorProps {
  spreadsheets: SpreadsheetRef[];
  onChange: (spreadsheets: SpreadsheetRef[]) => void;
}

const inputCls =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400';

const SpreadsheetIdEditor: React.FC<SpreadsheetIdEditorProps> = ({ spreadsheets, onChange }) => {
  const [newName, setNewName] = useState('');
  const [newId, setNewId] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  const handleChangeField = (index: number, field: 'id' | 'name', value: string) => {
    onChange(spreadsheets.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const handleRemove = (index: number) => {
    onChange(spreadsheets.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setAddError(null);
    if (!newName.trim()) { setAddError('Name cannot be empty.'); return; }
    if (!newId.trim()) { setAddError('Spreadsheet ID cannot be empty.'); return; }
    onChange([...spreadsheets, { id: newId.trim(), name: newName.trim() }]);
    setNewName('');
    setNewId('');
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        The spreadsheet ID is the string between <code className="font-mono">/d/</code> and{' '}
        <code className="font-mono">/edit</code> in the Google Sheets URL. Never edit or delete the{' '}
        <strong>Doc ID</strong> column in the sheet — that column is managed by the backend.
      </p>

      {/* Existing entries */}
      {spreadsheets.length > 0 && (
        <div className="divide-y divide-gray-100 dark:divide-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {spreadsheets.map((entry, i) => (
            <div key={i} className="grid grid-cols-[1fr_2fr_auto] items-center gap-3 px-4 py-2.5 bg-white dark:bg-gray-900">
              <input
                type="text"
                value={entry.name}
                onChange={(e) => handleChangeField(i, 'name', e.target.value)}
                placeholder="Name"
                className={inputCls}
              />
              <input
                type="text"
                value={entry.id}
                onChange={(e) => handleChangeField(i, 'id', e.target.value)}
                placeholder="Spreadsheet ID"
                className={`${inputCls} font-mono text-xs`}
              />
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0"
                title="Remove"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add new entry */}
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Add spreadsheet
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Patients 2026"
              className={inputCls}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-500 dark:text-gray-400">Spreadsheet ID</label>
            <input
              type="text"
              value={newId}
              onChange={(e) => setNewId(e.target.value)}
              placeholder="Paste spreadsheet ID"
              className={`${inputCls} font-mono text-xs`}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
          </div>
        </div>
        {addError && (
          <p className="text-xs text-red-500 flex items-center gap-1">
            <ExclamationCircleIcon className="w-3.5 h-3.5 flex-shrink-0" />
            {addError}
          </p>
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
        >
          <PlusIcon className="w-4 h-4" />
          Add
        </button>
      </div>
    </div>
  );
};

export default SpreadsheetIdEditor;
